import { motion } from "framer-motion";
import { useLang } from "../../i18n/LanguageContext";

/**
 * Decorative animated Pentomino "P" shape: five squares, the bottom-left (piece 1,
 * the inlet) in coral per the brand manual. Used in the hero and section accents.
 * Labels are bilingual (ES/EN) via the i18n context.
 */
export const PentominoMark = ({ size = 320, animate = true, className = "" }) => {
    const { t } = useLang();
    const marks = t.pentomino.marks;
    const cell = size / 2.2;
    const gap = size * 0.028;

    // Grid areas per manual: col/row + label number of the "P" piece
    const cells = [
        { col: 0, row: 0, label: "3", kind: "ink" },
        { col: 1, row: 0, label: "4", kind: "ink" },
        { col: 0, row: 1, label: "2", kind: "ink" },
        { col: 1, row: 1, label: "5", kind: "ink" },
        { col: 0, row: 2, label: "1", kind: "coral" },
    ];

    const ease = [0.22, 1, 0.36, 1];

    return (
        <div
            className={`relative ${className}`}
            style={{
                width: `${cell * 2 + gap}px`,
                height: `${cell * 3 + gap * 2}px`,
                maxWidth: "100%",
            }}
            aria-hidden="true"
            data-testid="pentomino-mark"
        >
            {cells.map((c, i) => (
                <motion.div
                    key={i}
                    initial={animate ? { opacity: 0, y: 12, scale: 0.9 } : false}
                    animate={animate ? { opacity: 1, y: 0, scale: 1 } : undefined}
                    transition={{
                        duration: 0.5,
                        ease,
                        delay: 0.1 + i * 0.1,
                    }}
                    className="absolute flex flex-col justify-between p-[9%] text-white"
                    style={{
                        left: c.col * (cell + gap),
                        top: c.row * (cell + gap),
                        width: cell,
                        height: cell,
                        background:
                            c.kind === "coral"
                                ? "var(--pt-coral)"
                                : "var(--pt-tinta)",
                        borderRadius: cell * 0.09,
                        boxShadow:
                            c.kind === "coral"
                                ? "0 24px 40px -18px rgba(240,70,77,0.6)"
                                : "0 20px 36px -22px rgba(20,35,64,0.5)",
                    }}
                    data-testid={`pentomino-cell-${c.label}`}
                >
                    <span
                        className="font-semibold leading-none"
                        style={{
                            fontStretch: "118%",
                            fontWeight: 640,
                            fontSize: cell * 0.28,
                        }}
                    >
                        {c.label}
                    </span>
                    <span
                        className="opacity-85 leading-none"
                        style={{
                            fontSize: cell * 0.11,
                            fontWeight: 600,
                            letterSpacing: "0.02em",
                        }}
                    >
                        {marks[c.label]}
                    </span>
                </motion.div>
            ))}
        </div>
    );
};
